import React, { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import BackspaceIcon from "@mui/icons-material/Backspace";
import { setEdgePin } from "../../../ducks/authenticationDuck";
import { pincodeApi } from "../../../actions/apiActions";

const KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  [null, "0", "backspace"],
];

const Numpad = ({ onKey }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
    {KEYS.map((row, rowIdx) => (
      <Box key={rowIdx} sx={{ display: "flex", gap: 1 }}>
        {row.map((key, colIdx) => {
          if (key === null) {
            return <Box key={colIdx} sx={{ width: 64, height: 48 }} />;
          }
          if (key === "backspace") {
            return (
              <IconButton
                key={colIdx}
                onClick={() => onKey("backspace")}
                sx={{ width: 64, height: 48, borderRadius: 1, border: "1px solid", borderColor: "divider" }}
              >
                <BackspaceIcon />
              </IconButton>
            );
          }
          return (
            <Button
              key={colIdx}
              variant="outlined"
              onClick={() => onKey(key)}
              sx={{ width: 64, height: 48, fontSize: "1.2rem", minWidth: 0 }}
            >
              {key}
            </Button>
          );
        })}
      </Box>
    ))}
  </Box>
);

const ChangePinDialog = ({ open, onClose, baseUrl }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();

  // "checking" → loading status from backend
  // "current"  → pincode is in use, verify existing PIN against backend
  // "new"      → enter the new PIN
  const [step, setStep] = useState("checking");
  const [pin, setPin] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Check backend pincode status whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setPin("");
    setErrorMsg("");
    setStep("checking");
    pincodeApi.getStatus(baseUrl)
      .then((status) => setStep(status?.pincodeEnabled ? "current" : "new"))
      .catch(() => setStep("new")); // if unreachable, skip current PIN step
  }, [open, baseUrl]);

  const handleKey = useCallback((key) => {
    setErrorMsg("");
    if (key === "backspace") {
      setPin((prev) => prev.slice(0, -1));
    } else {
      setPin((prev) => prev + key);
    }
  }, []);

  const handleNext = useCallback(async () => {
    if (step === "current") {
      setLoading(true);
      try {
        await pincodeApi.validate(baseUrl, pin);
        setPin("");
        setErrorMsg("");
        setStep("new");
      } catch {
        setErrorMsg(t("Incorrect PIN"));
      } finally {
        setLoading(false);
      }
    } else if (step === "new") {
      if (pin.length === 0) return;
      setLoading(true);
      try {
        await pincodeApi.change(baseUrl, pin);
        dispatch(setEdgePin(pin));
        handleClose();
      } catch {
        setErrorMsg(t("Failed to save PIN"));
      } finally {
        setLoading(false);
      }
    }
  }, [step, pin, baseUrl, dispatch, t]);

  const handleClose = useCallback(() => {
    setPin("");
    setErrorMsg("");
    onClose();
  }, [onClose]);

  if (step === "checking") {
    return (
      <Dialog open={open} maxWidth="xs" fullWidth>
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const title = step === "current" ? t("Enter Current PIN") : t("Enter New PIN");
  const submitLabel = step === "current" ? t("Next") : t("Save");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {errorMsg && <Alert severity="error" sx={{ mb: 1 }}>{errorMsg}</Alert>}
        <TextField
          fullWidth
          type="password"
          value={pin}
          slotProps={{ input: { readOnly: true } }}
          sx={{ mb: 2, mt: 1 }}
        />
        <Numpad onKey={handleKey} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>{t("Cancel")}</Button>
        <Button onClick={handleNext} variant="contained" disabled={pin.length === 0 || loading}>
          {loading ? <CircularProgress size={18} /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePinDialog;
